import { useState } from 'react';
import { Alert, Button, Checkbox, InputNumber, Segmented, Select, Switch } from 'antd';

import DateField from '../common/DateField';
import {
	buildRelativeBound,
	describeBoundOption,
	MINUTE_STEPS,
	parseRelativeBound,
	rangeEndFor,
	rangeStartFor,
	relativeUnitsFor,
} from '../../utils/dateBounds';
import {
	allowsMultipleDates,
	describeWeekdays,
	MULTI_DATE_TYPES,
	precisionOf,
	WEEKDAY_INITIALS,
	WEEKDAY_NAMES,
	WEEKDAYS_ONLY,
	weekdaysOf,
} from '../../utils/dateOptions';

/**
 * The card-definition sub-editor for a `date`, `date-time` or `time`
 * element's options — the bounds, the range pairing and the clock's step
 * (docs/plans/card-editor-dates-groups-filters.md Part 2c). The
 * `MeasurementOptions` mould: no state of its own beyond which KIND of bound
 * is being authored, every write through the form's path-based onChange.
 *
 *   min / max     Earliest and Latest. Four kinds, one Segmented:
 *                   None
 *                   A date       { kind: 'literal',  value }  — the element's
 *                                stored shape, entered in the platform's own
 *                                date field, so the grammar is the same one
 *                                the item form takes.
 *                   Relative     { kind: 'relative', value }  — 'today',
 *                                'today-30d', and for a date-time 'now+2h'.
 *                                "No past dates" IS `min: today`.
 *                   Another      { kind: 'element', name, inclusive } — a
 *                   element      same-type date element on this card or an
 *                                ancestor. "Allow equal" is `inclusive`.
 *   rangeEnd      on the START element: the sibling that holds the end of the
 *                 span. "Create an end element" appends "<label> end" right
 *                 after this row and links it in one act, because the common
 *                 case is a card that has no end element yet.
 *   minuteStep    1 · 5 · 15 · 30 — the clock's columns and the step buttons'
 *                 alignment. Replaces the hard-coded 5.
 *
 * A range END shows neither an Earliest row nor a Range toggle: its floor is
 * its start (enforced without anybody configuring it), and a chain of spans is
 * refused by the contract. The row says so in one muted line instead of
 * offering controls that cannot apply.
 *
 * The SECOND pass adds three more (utils/dateOptions):
 *
 *   allowMultiple Multiple values — one item, several dates. A date holds
 *                 one value now (the owner's ruling, 2026-10-22; KPR-1146):
 *                 never offered to turn on, and shown only on a date that
 *                 still holds several, so it can be turned off (each item then
 *                 keeps its earliest date, through the save's B16 question).
 *   weekdays      `date` only: a seven-way row of days, "Weekdays only" as
 *                 the preset. The calendar greys the rest and a typed date
 *                 that lands on one is refused in the same words.
 *   precision     `date` only: Day · Month · Year. Display and ENTRY — the
 *                 calendar opens in that mode and the field reads "September
 *                 2026" — while the stored value stays a whole day, which is
 *                 what leaves sorting, KQL, formulas and exports alone.
 */

const BOUND_LABELS = { min: 'Earliest', max: 'Latest' };

// The words the help line calls this element's own values.
const TYPE_WORDS = { date: 'a date', 'date-time': 'a date and time', time: 'a time' };

const kindsFor = (dataType, { inGroup = false } = {}) => {
	const literal = dataType === 'time' ? 'A time' : 'A date';
	const options = [
		{ value: 'none', label: 'None' },
		{ value: 'literal', label: literal },
	];
	// A `time` has no day to be relative to: 'today' names a day, and a wall
	// clock does not carry one.
	if (dataType !== 'time') options.push({ value: 'relative', label: 'Relative to today' });
	// A column's bound is a date or a relative date, never another element: a
	// column sits in a group's entries, which the card's own elements cannot
	// be read from (the API refuses it — keepr-api utils/repeatingGroups.js).
	if (!inGroup) options.push({ value: 'element', label: 'Another element' });
	return options;
};

// What each grain does — and, for the two coarse ones, the reassurance that
// matters: the stored value is still a whole day, so nothing that sorts,
// filters or calculates on this element changes.
const PRECISION_HINTS = {
	day: 'The calendar picks a day, and the field reads "Sep 9, 2026".',
	month: 'The calendar picks a month and the field reads "September 2026". The first of that month is stored, so sorting and filters are unchanged.',
	year: 'The field takes a year and reads "2026". January 1 of that year is stored, so sorting and filters are unchanged.',
};

const PRESETS = {
	min: { label: 'No past dates', bound: { kind: 'relative', value: 'today' } },
	max: { label: 'No future dates', bound: { kind: 'relative', value: 'today' } },
};

/**
 * The relative-date composer: anchor (Today, or Now on a date-time), − / +,
 * the amount and the unit — the bound grammar ('today', 'today-30d',
 * 'now+2h'; utils/dateBounds). The Earliest / Latest rows below and a date's
 * Default value (DefaultValueField) share it, so one grammar reads the same
 * wherever it is written. `value` is the stored spelling; `onChange` gets the
 * next one.
 */
export function RelativeDateComposer({ dataType, value, onChange, label, size = 'middle' }) {
	const relative = parseRelativeBound(value || '') || { anchor: 'today', sign: 1, amount: 0, token: 'd' };
	const setRelative = parts => onChange(buildRelativeBound({ ...relative, ...parts }));
	return (
		<div className='date-options__bound-body date-options__composer'>
			<Select
				aria-label={`${label} anchor`}
				className='date-options__anchor'
				size={size}
				value={relative.anchor}
				onChange={anchor => setRelative({ anchor, token: anchor === 'now' ? relative.token : (relative.token === 'h' || relative.token === 'm' ? 'd' : relative.token) })}
				options={dataType === 'date-time'
					? [{ value: 'today', label: 'Today' }, { value: 'now', label: 'Now' }]
					: [{ value: 'today', label: 'Today' }]}
			/>
			<Segmented
				aria-label={`${label} direction`}
				size={size === 'large' ? 'large' : 'middle'}
				value={relative.sign < 0 ? 'before' : 'after'}
				onChange={next => setRelative({ sign: next === 'before' ? -1 : 1 })}
				options={[{ value: 'before', label: '−' }, { value: 'after', label: '+' }]}
			/>
			<InputNumber
				aria-label={`${label} offset`}
				className='date-options__offset'
				size={size}
				min={0}
				max={9999}
				precision={0}
				value={relative.amount}
				onChange={next => setRelative({ amount: Number.isFinite(next) ? next : 0 })}
			/>
			<Select
				aria-label={`${label} unit`}
				className='date-options__unit'
				size={size}
				value={relative.token}
				onChange={token => setRelative({ token })}
				options={relativeUnitsFor(relative.anchor)}
			/>
		</div>
	);
}

/** One bound row: the kind, the body that kind needs, and the plain-words help. */
function BoundRow({ side, dataType, bound, candidates, onWrite, idPrefix, inGroup = false }) {
	const storedKind = bound && typeof bound === 'object' && bound.kind ? bound.kind : null;
	// The kind being AUTHORED. A stored bound speaks for itself; the local
	// value only carries the gap between choosing a kind and filling it in
	// (a half-written bound is never stored — the API would have to decide
	// what an empty one means).
	const [pending, setPending] = useState(null);
	const kind = storedKind || pending || 'none';
	const help = describeBoundOption(bound, side, { mode: dataType, elements: candidates });

	const setKind = (next) => {
		setPending(next);
		if (next === 'none') onWrite(undefined);
		// Relative is complete the moment it is chosen — "today" is the
		// commonest bound there is ("no past dates"), not a placeholder.
		else if (next === 'relative') onWrite({ kind: 'relative', value: 'today' });
		else if (next !== storedKind) onWrite(undefined);
	};

	return (
		<div className='date-options__bound'>
			<div className='date-options__bound-head'>
				<label className='d-block'>{BOUND_LABELS[side]}</label>
				<Button
					size='small'
					type='text'
					className='date-options__preset'
					onClick={() => { setPending(null); onWrite(PRESETS[side].bound); }}
				>
					{PRESETS[side].label}
				</Button>
			</div>
			<Segmented
				aria-label={BOUND_LABELS[side]}
				value={kind}
				onChange={setKind}
				options={kindsFor(dataType, { inGroup })}
			/>

			{kind === 'literal' ? (
				<div className='date-options__bound-body'>
					<DateField
						mode={dataType}
						elementName={`${idPrefix}.${side}.value`}
						elementLabel={`${BOUND_LABELS[side]} value`}
						value={storedKind === 'literal' ? bound.value : null}
						onChange={(_, value) => onWrite(value ? { kind: 'literal', value } : undefined)}
					/>
				</div>
			) : null}

			{kind === 'relative' ? (
				<RelativeDateComposer
					dataType={dataType}
					label={BOUND_LABELS[side]}
					value={storedKind === 'relative' ? bound.value : ''}
					onChange={value => onWrite({ kind: 'relative', value })}
				/>
			) : null}

			{kind === 'element' ? (
				<div className='date-options__bound-body'>
					<Select
						aria-label={`${BOUND_LABELS[side]} element`}
						size='large'
						className='date-options__element'
						value={storedKind === 'element' ? bound.name : undefined}
						placeholder={candidates.length ? 'Choose an element' : 'No other date element of this type'}
						disabled={!candidates.length}
						onChange={name => onWrite({ kind: 'element', name, inclusive: storedKind === 'element' ? bound.inclusive !== false : true })}
						options={candidates.map(el => ({ value: el.name, label: el.label?.singular || el.name }))}
					/>
					<Checkbox
						className='date-options__inclusive'
						disabled={storedKind !== 'element'}
						checked={storedKind === 'element' ? bound.inclusive !== false : true}
						onChange={event => onWrite({ ...bound, kind: 'element', inclusive: event.target.checked })}
					>
						Allow equal
					</Checkbox>
				</div>
			) : null}

			{help ? (
				<div className='form-panel__hint'>
					Items must have {TYPE_WORDS[dataType] || 'a date'} {help}.
				</div>
			) : null}
		</div>
	);
}

export default function DateOptions({ index, element, onChange, elements = [], onCreateEndElement = null, saveError = null, basePath = null, inGroup = false, savedMulti = false }) {
	const dataType = element?.dataType;
	const options = element?.options || {};
	const prefix = `${basePath || `elements[${index}]`}.options`;
	const name = element?.name || '';

	// Every same-type date element the card can see (its own and its
	// ancestors'), minus this one: what a bound may point at, and what a range
	// may end at. An element that holds a LIST is not offered by either — a
	// bound and a span each compare one date to one date, and a list has no
	// single date to be.
	// (A column of a repeating group names no other element: `inGroup` offers
	// no element bound and no range.)
	const candidates = inGroup ? [] : (elements || []).filter(el => (
		el?.name && el.name !== name && el.dataType === dataType && !allowsMultipleDates(el)
	));

	// This element is somebody's range end: its floor is that start, and it may
	// not start a range of its own (no chains).
	const start = rangeStartFor(name, elements);
	const end = rangeEndFor({ ...element, name }, elements);
	const rangeEndName = options.rangeEnd;
	// A link to an element that is gone (deleted, renamed, retyped) is dead
	// config the save would carry: say so where it can be fixed.
	const danglingEnd = !!rangeEndName && !end;

	const [rangeOn, setRangeOn] = useState(!!rangeEndName);
	const showRange = dataType !== 'time' && !start && !inGroup;

	// ---- Multiple values ----

	// A date holds one value (the owner's ruling, 2026-10-22; KPR-1146). The
	// switch is never offered on a date that does not already hold several —
	// keepr-api refuses it as new on every door (400 invalid_options,
	// utils/elementOptionKeys.js) — and is shown only where the SAVED element
	// holds a list (`savedMulti`), so the author can turn it OFF: the save then
	// asks how to convert the stored lists (B16), and each item keeps its
	// earliest date. Until the save it can go back on, which is the stored
	// value again. A list of dates belongs in a repeating group.
	const multiOn = MULTI_DATE_TYPES.includes(dataType) && options.allowMultiple === true;
	const multiShown = MULTI_DATE_TYPES.includes(dataType) && (multiOn || savedMulti);

	// ---- Weekdays and precision (a `date` alone) ----

	const weekdays = weekdaysOf({ ...element, dataType, name });
	const [weekdaysOn, setWeekdaysOn] = useState(!!weekdays);
	const precision = precisionOf({ ...element, dataType, name });

	const setWeekday = (day, on) => {
		const next = new Set(weekdays || []);
		if (on) next.add(day);
		else next.delete(day);
		const list = [...next].sort((a, b) => a - b);
		// An empty set is not a rule — the API refuses it — so clearing the
		// last day removes the key and leaves the switch on, waiting.
		onChange(`${prefix}.weekdays`, list.length ? list : undefined);
	};

	const weekdayHint = weekdays
		? (describeWeekdays(weekdays)
			? `Items must have a date that falls on ${describeWeekdays(weekdays)}.`
			: 'All seven days is the same as no rule at all.')
		: (weekdaysOn ? 'Choose at least one day, or turn this off.' : '');

	const endCandidates = candidates.filter(el => (
		// Nothing that already starts a span of its own, and nothing already
		// claimed as another element's end.
		!el.options?.rangeEnd && (!rangeStartFor(el.name, elements) || el.name === rangeEndName)
	));

	const setRangeEnd = (value) => {
		if (value === '__create__') {
			if (onCreateEndElement) onCreateEndElement();
			return;
		}
		onChange(`${prefix}.rangeEnd`, value || undefined);
	};

	// The API's own refusal of these very options (400 invalid_options names
	// the element and says why — "options.min must not be after options.max"),
	// shown where the setting it is about is edited rather than only as a
	// toast the reader has to carry back here.
	const refusal = (saveError?.code === 'invalid_options' && saveError.element && saveError.element === name)
		? saveError
		: null;

	return (
		<div className='date-options'>
			<div className='micro-label card-def-element__section'>Dates</div>

			{refusal ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message='These date settings were refused'
					description={refusal.message || 'Check the Earliest, Latest and range settings below.'}
				/>
			) : null}

			{start ? (
				<div className='date-options__end-note form-panel__hint' data-testid='date-options-end-note'>
					End of {start.label?.singular || start.name}. Its floor is the start, so it has no Earliest of its own.
				</div>
			) : null}

			{danglingEnd ? (
				<Alert
					type='warning'
					showIcon
					className='mb-2'
					message='The end of this range is missing'
					description={`Nothing on this card is called "${rangeEndName}" any more, or it is no longer the same type. Choose another end, or turn the range off.`}
				/>
			) : null}

			{start ? null : (
				<BoundRow
					side='min'
					idPrefix={prefix}
					dataType={dataType}
					bound={options.min}
					inGroup={inGroup}
					candidates={candidates}
					onWrite={value => onChange(`${prefix}.min`, value)}
				/>
			)}

			<BoundRow
				side='max'
				idPrefix={prefix}
				dataType={dataType}
				bound={options.max}
				inGroup={inGroup}
				candidates={candidates}
				onWrite={value => onChange(`${prefix}.max`, value)}
			/>

			{showRange ? (
				<div className='date-options__range'>
					<div className='toggle-row'>
						<div className='toggle-row__text'>
							<span className='toggle-row__label'>This date starts a range</span>
							<span className='toggle-row__description'>
								{multiOn
									? 'A span has one start and one end, so a field that holds several dates cannot begin one.'
									: 'Two elements read as one span — "Sep 9 – Sep 12 · 3 days" — and the end can never fall before the start.'}
							</span>
						</div>
						<Switch
							aria-label='This date starts a range'
							disabled={multiOn}
							checked={rangeOn || !!rangeEndName}
							onChange={next => {
								setRangeOn(next);
								if (!next) onChange(`${prefix}.rangeEnd`, undefined);
							}}
						/>
					</div>
					{(rangeOn || rangeEndName) ? (
						<div className='date-options__bound-body'>
							<label className='d-block' htmlFor={`${prefix}.rangeEnd`}>Ends at</label>
							<Select
								id={`${prefix}.rangeEnd`}
								aria-label='Ends at'
								className='date-options__element'
								value={rangeEndName || undefined}
								placeholder='Choose the element that holds the end'
								onChange={setRangeEnd}
								options={[
									...endCandidates.map(el => ({ value: el.name, label: el.label?.singular || el.name })),
									{ value: '__create__', label: 'Create an end element' },
								]}
							/>
						</div>
					) : null}
				</div>
			) : null}

			{multiShown ? (
				<div className='date-options__multiple' data-testid='date-options-multiple'>
					<div className='toggle-row'>
						<div className='toggle-row__text'>
							<span className='toggle-row__label'>Multiple values</span>
							<span className='toggle-row__description'>
								A date now holds one value. Turn this off to keep each item&apos;s earliest date. For a list of dates, use a repeating group with a date column.
							</span>
						</div>
						<Switch
							aria-label='Multiple values'
							checked={multiOn}
							onChange={next => onChange(`${prefix}.allowMultiple`, next && savedMulti ? true : undefined)}
						/>
					</div>
				</div>
			) : null}

			{dataType === 'date' ? (
				<div className='date-options__weekdays'>
					<div className='date-options__bound-head'>
						<label className='d-block'>Weekdays</label>
						<Button
							size='small'
							type='text'
							className='date-options__preset'
							onClick={() => { setWeekdaysOn(true); onChange(`${prefix}.weekdays`, WEEKDAYS_ONLY); }}
						>
							Weekdays only
						</Button>
					</div>
					<div className='toggle-row'>
						<div className='toggle-row__text'>
							<span className='toggle-row__label'>Only certain days of the week</span>
							<span className='toggle-row__description'>
								The calendar greys every other day, and a date typed onto one is refused.
							</span>
						</div>
						<Switch
							aria-label='Only certain days of the week'
							checked={weekdaysOn || !!weekdays}
							onChange={next => {
								setWeekdaysOn(next);
								if (!next) onChange(`${prefix}.weekdays`, undefined);
							}}
						/>
					</div>
					{(weekdaysOn || weekdays) ? (
						<div className='date-options__bound-body date-options__weekday-row'>
							{WEEKDAY_INITIALS.map((initial, day) => (
								<Checkbox
									// Two of the seven captions are "T" and two are "S",
									// so the accessible name is the day's own.
									key={WEEKDAY_NAMES[day]}
									className='date-options__weekday'
									aria-label={WEEKDAY_NAMES[day]}
									checked={!!weekdays?.includes(day)}
									onChange={event => setWeekday(day, event.target.checked)}
								>
									{initial}
								</Checkbox>
							))}
						</div>
					) : null}
					{weekdayHint ? <div className='form-panel__hint'>{weekdayHint}</div> : null}
				</div>
			) : null}

			{dataType === 'date' ? (
				<div className='date-options__bound-body'>
					<label className='d-block'>Precision</label>
					<Segmented
						aria-label='Precision'
						value={precision}
						onChange={value => onChange(`${prefix}.precision`, value === 'day' ? undefined : value)}
						options={[
							{ value: 'day', label: 'Day' },
							{ value: 'month', label: 'Month' },
							{ value: 'year', label: 'Year' },
						]}
					/>
					<div className='form-panel__hint'>{PRECISION_HINTS[precision]}</div>
				</div>
			) : null}

			{dataType !== 'date' ? (
				<div className='date-options__bound-body'>
					<label className='d-block' htmlFor={`${prefix}.minuteStep`}>Minute step</label>
					<Select
						id={`${prefix}.minuteStep`}
						aria-label='Minute step'
						className='date-options__element'
						value={MINUTE_STEPS.includes(options.minuteStep) ? options.minuteStep : 1}
						onChange={value => onChange(`${prefix}.minuteStep`, value === 1 ? undefined : value)}
						options={MINUTE_STEPS.map(step => ({
							value: step,
							label: step === 1 ? 'Every minute' : `${step} minutes`,
						}))}
					/>
					<div className='form-panel__hint'>The minutes the clock offers, and what the step buttons move by.</div>
				</div>
			) : null}
		</div>
	);
}
